import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardActionArea from '@material-ui/core/CardActionArea'
import { makeStyles, Theme } from '@material-ui/core/styles/'
import Typography from '@material-ui/core/Typography'
import React, {
  lazy,
  Suspense,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useHistory } from 'react-router-dom'
import { Settings } from 'react-slick'

import { createSlickArrows } from '../../../../Components/SlickArrows'
import colorContext from '../../../../Context/colorContext'
import useSlickA11y from '../../../../hooks/useSlickA11y'
import { AppsI } from '../..'

const Slider = lazy(() => import('react-slick'))

interface Props {
  listApps: AppsI
}

export default function Apps({ listApps }: Props): JSX.Element {
  const { colors } = useContext(colorContext)
  const useStyles = makeStyles((theme: Theme) => ({
    main: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: colors.colorBackground,
      alignItems: 'center',
      padding: '40px 5px 108px',
    },
    card: {
      position: 'relative',
      marginBottom: 10,
      borderRadius: 10,
      boxShadow: 'none',
      textTransform: 'capitalize',
      marginTop: 10,
      height: 146,
      minWidth: 189,
      width: '100%',
      color: colors.accentColor,
      fontSize: theme.typography.pxToRem(18),
      backgroundColor: colors.colorBackgroundSecundary,
      '&:hover': {
        boxShadow: '0px 12px 42px -4px rgba(24, 39, 75, 0.12)',
        backgroundColor: colors.colorBackground,
      },
    },
    textTitle: {
      position: 'relative',
      textTransform: 'uppercase',
      fontFamily: 'Roboto',
      padding: '10px 0px 0px 3px',
      marginBottom: 5,
      fontWeight: 'bold',
      color: colors.textBody,
      fontSize: theme.typography.pxToRem(36),
      left: 8,
      '&::after': {
        content: '""',
        display: 'block',
        width: 4,
        height: 30,
        backgroundColor: colors.accentColor,
        position: 'absolute',
        bottom: 11,
        marginLeft: -14,
        left: 5,
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: theme.typography.pxToRem(28),
        marginBottom: 9,
        '&::after': {
          bottom: 4,
        },
      },
    },
    textSubTitle: {
      position: 'relative',
      fontFamily: 'Roboto',
      padding: '10px 0px',
      fontSize: theme.typography.pxToRem(18),
      color: colors.subTitleColor,
      letterSpacing: '0.1px',
      lineHeight: '24px',
      fontWeight: 400,
    },
    button: {
      border: `1px solid ${colors.textAccentColor}`,
      minWidth: 0,
      marginBottom: 10,
      padding: '8px 35px',
      borderRadius: 5,
      '&:hover': {
        background: colors.colorBackgroundSecundary,
        color: colors.accentColor,
      },
    },
    icon: {
      width: 24,
      height: 24,
    },
    box: {
      display: 'flex',
      marginBottom: '50px',
      flexDirection: 'column',
      [theme.breakpoints.down('sm')]: {
        alignItems: 'center',
      },
    },
    textList: {
      marginRight: 8,
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 700,
      marginLeft: 8,
      textTransform: 'uppercase',
      color: colors.accentColor,
      '&:hover': {
        color: colors.textAccentColor,
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: theme.typography.pxToRem(14),
      },
    },
    buttonMore: {
      maxWidth: 360,
      width: '100%',
      marginTop: 80,
      height: 42,
      borderRadius: 25,
      backgroundColor: colors.colorWithoutGradient,
      alignSelf: 'center',
      color: colors.accentColor,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textDecoration: 'none',
      marginRight: 8,
      marginLeft: 8,
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: colors.colorWithoutGradient,
      },
    },
  }))
  const classes = useStyles()
  const [appSelected, setAppSelected] = useState<number | null>()
  const history = useHistory()
  const sliderRef = useRef<HTMLDivElement>(null)
  useSlickA11y(sliderRef)
  const { NextArrow, PrevArrow } = useMemo(
    () =>
      createSlickArrows({
        nextLabel: 'Próximo app',
        prevLabel: 'App anterior',
      }),
    [],
  )

  const settings: Settings = {
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    speed: 1000,
    slidesToShow: 5,
    slidesToScroll: 1,
    autoplay: true,
    pauseOnHover: true,
    pauseOnFocus: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: true,
          dots: false,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 2,
          infinite: true,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: true,
        },
      },
    ],
  }
  return (
    <Box className={classes.main}>
      <Box display="flex" maxWidth="1320px" width="100%" flexDirection="column">
        <Box className={classes.box}>
          <Typography className={classes.textTitle} variant="h5" component="h2">
            Apps do governo
          </Typography>
          <Typography className={classes.textSubTitle}>
            Baixe nossos apps
          </Typography>
        </Box>
        <div
          ref={sliderRef}
          role="region"
          aria-roledescription="carrossel"
          aria-label="Apps do governo"
        >
          <Suspense fallback="">
            <Slider {...settings}>
              {listApps.results
                .filter((app) => app.ativo)
                .map((app) => (
                  <Card
                    onMouseEnter={(): void => setAppSelected(app.id)}
                    onMouseLeave={(): void => setAppSelected(null)}
                    onFocus={(): void => setAppSelected(app.id)}
                    onBlur={(): void => setAppSelected(null)}
                    key={app.id}
                    className={classes.card}
                  >
                    <Box
                      position="absolute"
                      height="100%"
                      width="100%"
                      display={appSelected === app.id ? 'flex' : 'none'}
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="space-around"
                      zIndex="1"
                      style={{
                        background: colors.cardAccentColor,
                        backgroundSize: 'cover',
                      }}
                    >
                      <Typography
                        style={{
                          color: colors.textAccentColor,
                          textTransform: 'none',
                          fontSize: 14,
                          fontWeight: 'bold',
                          padding: '0px 5px',
                        }}
                        align="center"
                      >
                        {app.nome}
                      </Typography>
                      <Box display="flex" flexDirection="column">
                        <Button
                          onClick={() =>
                            history.push(`/aplicativo/${app.slug}`)
                          }
                          className={classes.button}
                          variant="outlined"
                          tabIndex={appSelected === app.id ? 0 : -1}
                        >
                          {`Ver detalhes do app ${app.nome}`}
                        </Button>
                      </Box>
                    </Box>
                    <CardActionArea
                      style={{
                        height: '100%',
                      }}
                    >
                      <img
                        width="100%"
                        height="100%"
                        src={app.imagem}
                        alt={app?.nome ?? ''}
                        loading="lazy"
                      />
                    </CardActionArea>
                  </Card>
                ))}
            </Slider>
          </Suspense>
        </div>
        <a href="/aplicativos" className={classes.buttonMore} rel="noreferrer">
          <p className={classes.textList}>VEJA TODOS OS APLICATIVOS</p>
        </a>
      </Box>
    </Box>
  )
}
